// PostHog product analytics: pageviews (for DAU/WAU/MAU) and custom events.
//  - Plain: project_created, ahu_created, project_duplicated, report_generated,
//    the three kai_assist_* events and kai_select_design_run (below).
//  - Usage events (EVENT_SCHEMAS, ksdm#1709): what users do on the AHU board,
//    CAD viewer, project board and sizer, so we know which flows to test.
//    section_added / _moved / _deleted / _bulk_deleted / _duplicated,
//    component_added / _rated / _selected, accessory_added, ahu_duplicated,
//    costing_run, cad_exported, model_view_shared, sizer_applied.
//    Each fires once the server confirms the action. component_rated is the
//    only one with a success flag: the other slow jobs (CAD, costing,
//    duplication) report only success, so their failures are not visible here.
//
// Adding an event: see "PostHog events" in the repo CLAUDE.md.
// ahu_created fires on every path that POSTs a new AHU (ahu/units/), once the
// server confirms it: the project board's AHU list (AhuListPanel), the AHU
// board's "Create New..." breadcrumb (AhuNameBreadcrumb) and a completed KAI
// Select design (useAhuDesign; a failed design deletes its AHU, so it is not
// counted). Duplicating an AHU is not a creation here.
//
// AI features:
//  - kai_assist_opened / kai_assist_conversation_started /
//    kai_assist_message_sent come from the KAI Assist
//    chatbot widget, not from this app. The widget (productgenie-workspace
//    packages/chatbot-snippet, loaded by templates/common/react.html) mounts in
//    a shadow root in this same document and dispatches the payload-free
//    window events `kai-assist:opened` (the user opened the panel),
//    `kai-assist:conversation-started` (the first message of a new
//    conversation: first use, "New chat", or after the 24 h session expiry;
//    not reloads/new tabs/History, which continue a conversation) and
//    `kai-assist:message-sent` (one user message sent). That is its public
//    analytics contract; listenToKaiAssist() forwards each one with NO
//    properties. An older widget that does not dispatch them just means those
//    events never fire.
//  - kai_select_design_run fires once per KAI Select design run that ends
//    (useAhuDesign), with `success` (boolean) as its only property. A run the
//    user cancels is not a run. A successful run also fires ahu_created: they
//    mean different things (a design ran / an AHU exists).
//  - KAI Select page usage needs no event: it is the $pageview on /kai-select/.
//
// Off unless Django injected __KSDM_CONFIG__.posthog (KSDM_POSTHOG_KEY set).
// When off, posthog-js is never even downloaded: it is a dynamic import, so it
// lands in its own chunk, and sdmk.react_views._vite_assets deliberately does
// not modulepreload dynamic imports below the entry.
//
// Everything else posthog-js can collect is pinned OFF in the init options
// below, and each pin is a client value that the PostHog project's remote
// settings cannot override (checked against the posthog-js 1.434.15 source):
// session replay, console logs, autocapture/rage clicks, heatmaps, dead clicks,
// exception autocapture, web vitals / performance, surveys, product tours,
// conversations, web experiments, site apps. On top of that the remote config
// is never fetched (advanced_disable_flags) and no external script (recorder,
// logs, surveys, toolbar...) can be loaded (disable_external_dependency_loading).
//
// Privacy: no customer project names, AHU names/tags, page titles, URL query
// strings or hashes, usernames, emails or free text may reach PostHog.
//  - scrubEvent (before_send) runs on every event, person updates included:
//     - deletes `title` (posthog-js adds document.title to every $pageview,
//       and the boards put the project name and AHU tag in the title) and
//       `$el_text`, from properties, $set and $set_once;
//     - deletes the values posthog-js reads out of URL queries: campaign
//       params (utm_*, gclid...) and the referrer search keyword, including
//       their $initial_* / $session_entry_* copies;
//     - reduces every URL-valued property ($current_url, $referrer,
//       $initial_*, $session_entry_*...) to origin + pathname: no query
//       (project search sends the typed name as `?name=`, a CAD share id is
//       `?id=`) and no hash.
//    If it throws, posthog-js drops the event (fails closed).
//  - Client IP / location: nothing in the browser can keep the IP from
//    PostHog, because ingestion reads it from the HTTP request (posthog-js's
//    old `ip` option has no effect; 1.434.15 only logs a deprecation warning).
//    Two things keep it out of the data:
//     - the PostHog project setting "Discard client IP data" (Settings →
//       Project) must be ON before a KSDM_POSTHOG_KEY is set anywhere. It is
//       on for the KSDM project (since 2026-09-27); with it, no $ip is stored;
//     - every event carries the super property `$geoip_disable: true` (see
//       identify()), so ingestion derives no city/region/country, even if
//       the project setting is ever switched off. That alone would still
//       store the IP itself: the project setting is the real control.
//  - The person is `<server name>:<user id>` with a `tenant` property only.
//  - Custom events carry no ids or user data. Properties are only what an
//    event declares in EVENT_SCHEMAS (slugs from code or catalogs, counts,
//    booleans, durations), rebuilt by usageProperties(), plus report_generated's
//    report TYPE from the report catalog (reportProperties()) and
//    kai_select_design_run's `success`. Durations and manufacturer catalog
//    slugs are allowed (not personal data, decided 2026-10-03).

import type { CaptureResult, PostHog, Properties } from 'posthog-js';
import { getKsdmConfig } from 'helpers/index';

let client: PostHog | null = null;
let loading: Promise<PostHog | null> = Promise.resolve(null);

export function initAnalytics(): void {
  const config = getKsdmConfig();
  const ph = config?.posthog;
  if (!config || !ph?.key) return;

  // Before the import, so an open/send while posthog-js is still loading is
  // queued by capture() rather than missed.
  listenToKaiAssist();

  loading = import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(ph.key, {
        api_host: ph.host,
        defaults: '2026-05-30',
        person_profiles: 'identified_only',
        // Initial pageview + one per pathname change. The SPA routes through
        // `history` (src/history.ts), which calls the pushState/replaceState
        // PostHog patches. Hash-only changes (board tabs) are NOT pageviews.
        capture_pageview: 'history_change',
        capture_pageleave: false,
        // One persistence per tenant host. The default cross-subdomain cookie
        // would share one identity across every *.kinetix-air.com tenant.
        cross_subdomain_cookie: false,

        // ---- everything else OFF, whatever the PostHog project says ----
        // Never fetch the remote config (/array/<token>/config), so no project
        // setting can switch anything on. This also covers the Logs product's
        // console capture, which a local option cannot veto once remote
        // config says `captureConsoleLogs: true` (posthog-logs.js).
        advanced_disable_flags: true,
        // No lazy scripts: recorder, logs, surveys, toolbar, web vitals...
        disable_external_dependency_loading: true,
        autocapture: false,
        rageclick: false,
        capture_heatmaps: false,
        capture_dead_clicks: false,
        capture_exceptions: false,
        // Web vitals and network timing.
        capture_performance: false,
        disable_session_recording: true,
        enable_recording_console_log: false,
        disable_surveys: true,
        disable_product_tours: true,
        disable_conversations: true,
        disable_web_experiments: true,
        opt_in_site_apps: false,
        disable_scroll_properties: true,
        // UTM / click-id values are read from the URL query.
        save_campaign_params: false,

        before_send: scrubEvent,
      });
      identify(posthog, config);
      client = posthog;
      return posthog;
    })
    .catch(() => null);
}

// ---- privacy scrubbing (see the header) ------------------------------------

/** Auto-collected properties that carry document.title or DOM text.
 * posthog-js 1.434.15 sets `title` on every $pageview. */
const TEXT_PROPERTIES = new Set(['title', '$el_text']);

/** Properties posthog-js reads out of a URL query (the page's or the
 * referrer's): the campaign params (CAMPAIGN_PARAMS in
 * @posthog/browser-common utils/event-utils) and the search keyword. They
 * also arrive as `$initial_<name>` and `$session_entry_<name>`, whatever
 * save_campaign_params says. */
const QUERY_PROPERTIES = new Set([
  'ph_keyword',
  'gad_source',
  'mc_cid',
  'gclid',
  'gclsrc',
  'dclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
  'twclid',
  'li_fat_id',
  'igshid',
  'ttclid',
  'rdt_cid',
  'epik',
  'qclid',
  'sccid',
  'irclid',
  '_kx',
]);

function isDroppedProperty(key: string): boolean {
  if (TEXT_PROPERTIES.has(key)) return true;
  const name = key.replace(/^\$(initial_|session_entry_)?/, '');
  return name.startsWith('utm_') || QUERY_PROPERTIES.has(name);
}

/** An absolute http(s) URL becomes origin + pathname; one that does not parse
 * becomes '' (fail closed). Anything else is returned unchanged. */
function scrubUrl(value: string): string {
  if (!/^https?:\/\//i.test(value)) return value;
  try {
    const url = new URL(value);
    return url.origin + url.pathname;
  } catch {
    return '';
  }
}

function scrubUrls(value: unknown, depth: number): unknown {
  if (typeof value === 'string') return scrubUrl(value);
  if (depth <= 0 || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) value[i] = scrubUrls(value[i], depth - 1);
    return value;
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) record[key] = scrubUrls(record[key], depth - 1);
  return record;
}

/** before_send: runs on every event ($pageview, $identify, custom events). */
function scrubEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  const props = event.properties as Properties | undefined;
  for (const bag of [props, event.$set, event.$set_once, props?.$set, props?.$set_once]) {
    if (!bag || typeof bag !== 'object') continue;
    for (const key of Object.keys(bag)) {
      if (isDroppedProperty(key)) delete (bag as Properties)[key];
    }
  }
  scrubUrls(event.properties, 6);
  if (event.$set) scrubUrls(event.$set, 6);
  if (event.$set_once) scrubUrls(event.$set_once, 6);
  return event;
}

// ---- identity ---------------------------------------------------------------

/** Super properties on every event. reset() clears them (persistence.clear()
 * empties the props), so they are registered after every reset. */
let superProperties: Properties = {};

function registerSuperProperties(posthog: PostHog): void {
  posthog.register(superProperties);
}

/** Runs synchronously right after posthog.init(), so it finishes before the
 * initial $pageview, which posthog-js defers to the next tick. */
function identify(posthog: PostHog, config: KsdmConfig): void {
  const tenant = config.serverName;
  superProperties = {
    // PostHog ingestion skips GeoIP enrichment for an event carrying this
    // (the same property PostHog's server SDKs send). See the header.
    $geoip_disable: true,
    tenant,
    environment: config.environment ?? '',
    app_version: config.versions?.software ?? '',
  };
  try {
    const user = config.user;
    const identified = posthog.get_property('$user_state') === 'identified';
    if (!user || user.id == null) {
      // Anonymous page (login, reset password...). An identity left over from
      // a logout that skipped resetAnalytics() (session expiry, a
      // Django-rendered page) must not carry over to whoever logs in next.
      if (identified) posthog.reset();
      registerSuperProperties(posthog);
      return;
    }
    // User ids are per tenant database and collide across tenants, so the
    // distinct id is namespaced by server name. A different user on this
    // browser starts from a clean identity.
    const distinctId = `${tenant}:${String(user.id)}`;
    if (identified && posthog.get_distinct_id() !== distinctId) posthog.reset();
    registerSuperProperties(posthog);
    // Deliberately nothing else: no username, email, name or role.
    posthog.identify(distinctId, { tenant });
  } catch {
    /* analytics must never break the app */
  }
}

/** Forget the identified user. Call on logout, before navigating away. */
export function resetAnalytics(): void {
  const reset = (posthog: PostHog) => {
    try {
      posthog.reset();
      registerSuperProperties(posthog);
    } catch {
      /* analytics must never break the app */
    }
  };
  if (client) reset(client);
  else void loading.then((posthog) => posthog && reset(posthog));
}

// ---- custom events ----------------------------------------------------------

/** A report type from the report catalog (ksdm_master_ahu_report): never
 * anything the user typed. */
export interface ReportType {
  name: string;
  value: string;
}

/** The ONLY properties report_generated sends. To stop sending them, make this
 * return {}. */
function reportProperties(report: ReportType): Properties {
  return { report_name: report.name, report_value: report.value };
}

function capture(event: string, properties?: Properties): void {
  const send = (posthog: PostHog | null) => {
    try {
      posthog?.capture(event, properties);
    } catch {
      /* analytics must never break the app */
    }
  };
  if (client) send(client);
  else void loading.then(send);
}

/** The ONLY property kai_select_design_run sends. */
export interface DesignRunResult {
  success: boolean;
}

type PlainEvent =
  | 'project_created'
  | 'ahu_created'
  | 'project_duplicated'
  | 'kai_assist_opened'
  | 'kai_assist_conversation_started'
  | 'kai_assist_message_sent';

// ---- usage events with properties (AHU board, CAD viewer, sizer...) --------
//
// Each event declares the ONLY properties it may send and their kind. track()
// rebuilds the properties from this list, so a field that is not declared never
// leaves the browser, whatever the call site passes:
//  - 'slug': a code or catalog value (section/component/accessory type, file
//    format, manufacturer catalog slug). Lower-cased, reduced to [a-z0-9_],
//    capped at 40 characters; never a name, tag or anything the user typed.
//  - 'slugs': a list of slugs, de-duplicated and sorted.
//  - 'count' / 'ms': a non-negative whole number (ms = a duration).
//  - 'bool': true only for a literal true.
const EVENT_SCHEMAS = {
  section_added: { section_type: 'slug', hybrid: 'bool' },
  section_moved: { section_type: 'slug' },
  section_deleted: { section_type: 'slug' },
  section_bulk_deleted: { count: 'count' },
  section_duplicated: { section_type: 'slug' },
  component_added: { component: 'slug' },
  component_rated: {
    component: 'slug',
    manufacturer: 'slug',
    success: 'bool',
    results: 'count',
    duration_ms: 'ms',
    source: 'slug',
  },
  component_selected: { component: 'slug' },
  accessory_added: { accessory_type: 'slug' },
  ahu_duplicated: { target: 'slug' },
  costing_run: { trigger: 'slug' },
  cad_exported: { format: 'slug' },
  model_view_shared: { days: 'count' },
  sizer_applied: { components: 'slugs', tunnels: 'count' },
} as const;

type PropKind = 'slug' | 'slugs' | 'count' | 'ms' | 'bool';
type PropValue<K> = K extends 'slug'
  ? string
  : K extends 'slugs'
    ? readonly string[]
    : K extends 'bool'
      ? boolean
      : number;

/** An event that carries declared properties. */
export type UsageEvent = keyof typeof EVENT_SCHEMAS;
/** The properties a usage event takes, typed from its schema. */
export type UsageProperties<E extends UsageEvent> = {
  -readonly [P in keyof (typeof EVENT_SCHEMAS)[E]]: PropValue<(typeof EVENT_SCHEMAS)[E][P]>;
};

function toSlug(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  return slug || undefined;
}

function toCount(value: unknown, max: number): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return undefined;
  return Math.min(Math.round(value), max);
}

function usageProperties(event: UsageEvent, detail: Record<string, unknown>): Properties {
  const schema = EVENT_SCHEMAS[event] as Record<string, PropKind>;
  const out: Properties = {};
  for (const [key, kind] of Object.entries(schema)) {
    const value = detail[key];
    let clean: unknown;
    if (kind === 'slug') clean = toSlug(value);
    else if (kind === 'slugs')
      clean = Array.isArray(value)
        ? [...new Set(value.map(toSlug).filter((s): s is string => !!s))].sort()
        : undefined;
    else if (kind === 'count') clean = toCount(value, 10_000);
    else if (kind === 'ms') clean = toCount(value, 3_600_000);
    else clean = value === true;
    if (clean !== undefined) out[key] = clean;
  }
  return out;
}

/** Aliases → the one analytics name; see analyticsComponentName(). */
const COMPONENT_ALIASES: Record<string, string> = {
  er_wheel: 'energy_wheel',
  rotary: 'energy_wheel',
  erwheel: 'energy_wheel',
  energy_wheels: 'energy_wheel',
  plate: 'plate_heat_exchanger',
  phx: 'plate_heat_exchanger',
  plate_heat_exchangers: 'plate_heat_exchanger',
  uds: 'user_defined_component',
  user_defined: 'user_defined_component',
  split_plenum: 'plenum',
};

/** One analytics name per component, whatever alias the call site holds: the
 * insert strip's values (er_wheel, rotary, plate, uds, split_plenum), rating
 * envelope parts (erwheel), and the server's section.type after a reload
 * (energy_wheel, plate_heat_exchanger, user_defined_component, plenum). Use it
 * for every section_type / component property so one component is one bucket. */
export function analyticsComponentName(value: string | null | undefined): string {
  if (!value) return 'unknown';
  return COMPONENT_ALIASES[value] ?? value;
}

function isUsageEvent(event: string): event is UsageEvent {
  return Object.prototype.hasOwnProperty.call(EVENT_SCHEMAS, event);
}

/**
 * Capture one of the custom events. Call only once the server has confirmed
 * the action. No-op when PostHog is off; before posthog-js has loaded, the
 * event waits on the import.
 */
export function track(event: PlainEvent): void;
export function track(event: 'report_generated', report: ReportType): void;
export function track(event: 'kai_select_design_run', result: DesignRunResult): void;
export function track<E extends UsageEvent>(event: E, detail: UsageProperties<E>): void;
export function track(
  event: PlainEvent | 'report_generated' | 'kai_select_design_run' | UsageEvent,
  detail?: ReportType | DesignRunResult | Record<string, unknown>
): void {
  // Callers run track() inside their own success handlers, so it must never
  // throw: an exception here would skip the app code after the call.
  try {
    let properties: Properties | undefined;
    if (event === 'report_generated' && detail && 'value' in detail) {
      properties = reportProperties(detail as ReportType);
    } else if (event === 'kai_select_design_run' && detail && 'success' in detail) {
      // Rebuilt, not passed through, so nothing else can ride along.
      properties = { success: (detail as DesignRunResult).success === true };
    } else if (isUsageEvent(event)) {
      properties = usageProperties(event, (detail ?? {}) as Record<string, unknown>);
    }
    capture(event, properties);
  } catch {
    /* analytics must never break the app */
  }
}

// ---- KAI Assist (chatbot widget) --------------------------------------------

/** Widget window event -> PostHog event. See the header. */
const KAI_ASSIST_EVENTS = {
  'kai-assist:opened': 'kai_assist_opened',
  'kai-assist:conversation-started': 'kai_assist_conversation_started',
  'kai-assist:message-sent': 'kai_assist_message_sent',
} as const;

let kaiAssistListening = false;

/** Registered once, and only when PostHog is on (called from initAnalytics).
 * The widget's event carries no detail and none is read: the PostHog event
 * has no properties. */
function listenToKaiAssist(): void {
  if (kaiAssistListening) return;
  kaiAssistListening = true;
  try {
    for (const [domEvent, event] of Object.entries(KAI_ASSIST_EVENTS)) {
      window.addEventListener(domEvent, () => track(event));
    }
  } catch {
    /* analytics must never break the app */
  }
}
